import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './TiktokComments.css';
import { FaHeart, FaRegHeart, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { SocialUserAvatar } from "./SocialUserAvatar";
import { SocialUserName } from "./SocialUserName";

// Composant personnalisé pour afficher le nom d'utilisateur avec le style approprié
const StyledUserName = ({ user_id }) => {
  const [userName, setUserName] = useState("");
  
  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const userResponse = await sdk.models.PeupleUser.getOne(user_id);
        if (userResponse) {
          setUserName(`${userResponse.first_name || ''} ${userResponse.last_name || ''}`.trim() || "Utilisateur");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération du nom d'utilisateur:", err);
        setUserName("Utilisateur");
      }
    };
    
    if (user_id) {
      fetchUserName();
    }
  }, [user_id]);
  
  return <span>{userName}</span>;
};

const TiktokComments = ({ post, isOpen, onClose, user }) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const commentsListRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && post) {
      fetchComments();
      
      // Focus l'input quand le panneau est ouvert
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 300);
    }
  }, [isOpen, post]);

  // Scroll to bottom when new comments are added or when panel opens
  useEffect(() => {
    if (commentsListRef.current && isOpen) {
      commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
    }
  }, [comments, isOpen]);

  const fetchComments = async () => {
    if (!post || isLoading) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      // Check if post already has comments loaded
      if (post.comments && post.comments.length > 0) {
        setComments(sortComments(post.comments));
      }
    } catch (err) {
      console.error("Error fetching comments:", err);
      setError("Impossible de charger les commentaires");
    } finally {
      setIsLoading(false);
    }
  };

  const sortComments = (commentList) => {
    // Sort comments by time, oldest first to mimic a conversation flow
    return [...commentList].sort((a, b) => a.time - b.time);
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || isSubmitting || !user) return;
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const commentData = {
        post_id: post.id,
        user_id: user.user_id,
        text: newComment.trim(),
        time: Math.floor(Date.now() / 1000),
      };
      
      const response = await sdk.models.SocialComment.create(commentData);
      
      if (response) {
        // Add new comment to list
        setComments([...comments, response]);
        setNewComment('');
        
        // Update comment count in post if needed
        if (typeof post.nbrComments !== 'undefined') {
          post.nbrComments = (post.nbrComments || 0) + 1;
        }
      }
    } catch (err) {
      console.error("Error posting comment:", err);
      setError("Impossible d'ajouter votre commentaire");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const toggleLike = async (comment) => {
    if (!user) return;
    
    // Optimistic UI update
    const updatedComments = comments.map(c => {
      if (c.id === comment.id) {
        return {
          ...c,
          hasLiked: !c.hasLiked,
          nbrLikes: c.hasLiked ? (c.nbrLikes - 1) : ((c.nbrLikes || 0) + 1)
        };
      }
      return c;
    });
    
    setComments(updatedComments);
    
    try {
      if (!comment.hasLiked) {
        await sdk.models.CommentLike.create({
          user_id: user.user_id,
          comment_id: comment.id
        });
      } else {
        await sdk.models.CommentLike.deleteMyLike({ comment_id: comment.id });
      }
    } catch (err) {
      console.error("Error toggling like:", err);
      // Rollback on error
      setComments(comments);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "À l'instant";
    
    const now = new Date();
    const commentTime = new Date(timestamp * 1000);
    const diffInSeconds = Math.floor((now - commentTime) / 1000);
    
    if (diffInSeconds < 60) {
      return "À l'instant";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}j`;
    } else {
      return commentTime.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    }
  };

  return (
    <>
      <div className={`comments-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose}></div>
      <div className={`tiktok-comments-container ${isOpen ? 'visible' : ''}`}>
        <div className="comments-header">
          <div className="comments-title">
            {comments.length} {comments.length === 1 ? 'commentaire' : 'commentaires'}
          </div>
          <button className="comments-close" onClick={onClose} aria-label="Fermer les commentaires">
            <FaTimes />
          </button>
        </div>
        
        <div className="comments-list" ref={commentsListRef}>
          {isLoading ? (
            <div className="no-comments">Chargement des commentaires...</div>
          ) : comments.length === 0 ? (
            <div className="no-comments">Pas encore de commentaires. Soyez le premier à commenter!</div>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="tiktok-comment-item">
                <div className="comment-avatar">
                  <SocialUserAvatar user_id={comment.user_id} />
                </div>
                <div className="comment-content">
                  <div className="comment-header">
                    <div className="comment-username">
                      <StyledUserName user_id={comment.user_id} />
                    </div>
                  </div>
                  <p className="comment-text">{comment.text}</p>
                  <div className="comment-footer">
                    <span className="comment-time">{formatTimestamp(comment.time)}</span>
                    <div className="comment-actions">
                      <button 
                        className={`like-button ${comment.hasLiked ? 'liked' : ''}`}
                        onClick={() => toggleLike(comment)}
                        aria-label={comment.hasLiked ? "Ne plus aimer" : "Aimer"}
                      >
                        {comment.hasLiked ? <FaHeart /> : <FaRegHeart />}
                      </button>
                      {comment.nbrLikes > 0 && (
                        <span className="likes-count">{comment.nbrLikes}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          
          {error && (
            <div className="comment-error">{error}</div>
          )}
        </div>
        
        <div className="comment-input-section">
          <div className="comment-input-avatar">
            {user && <SocialUserAvatar user_id={user.user_id} />}
          </div>
          <div className="comment-input-wrapper">
            <input
              type="text"
              ref={inputRef}
              className="comment-input"
              placeholder="Ajouter un commentaire..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isSubmitting || !user}
            />
          </div>
          <button
            className="send-button"
            onClick={handleSubmitComment}
            disabled={isSubmitting || !newComment.trim() || !user}
            aria-label="Envoyer le commentaire"
          >
            <FaPaperPlane />
          </button>
        </div>
      </div>
    </>
  );
};

export { TiktokComments };